import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { SignedIn, SignedOut, ClerkLoaded, ClerkLoading, AuthenticateWithRedirectCallback } from '@clerk/clerk-react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { trackPageView } from './utils/googleAnalytics';
import { useNeedsOnboarding } from './stores/intentStore';
import { OnboardingGuard, PassOnlyWizard } from './components/onboarding';

// Lazy-loaded page components
const Home = React.lazy(() => import('./pages/Home'));
const Pricing = React.lazy(() => import('./pages/Pricing'));
const CreateSignature = React.lazy(() => import('./pages/CreateSignature'));
const Test = React.lazy(() => import('./pages/Test'));
const Analytics = React.lazy(() => import('./pages/Analytics'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const DynamicSignatures = React.lazy(() => import('./pages/DynamicSignatures'));
const SignatureView = React.lazy(() => import('./pages/SignatureView'));
const CheckoutSuccess = React.lazy(() => import('./pages/CheckoutSuccess'));
const ManageSubscription = React.lazy(() => import('./pages/ManageSubscription'));
const TeamManagement = React.lazy(() => import('./pages/TeamManagement'));
const OutlookSignatureTest = React.lazy(() => import('./pages/OutlookSignatureTest'));
const Support = React.lazy(() => import('./pages/Support'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const TermsAndConditions = React.lazy(() => import('./pages/TermsAndConditions'));
const ContactUs = React.lazy(() => import('./pages/ContactUs'));
const FeedbackRoadmap = React.lazy(() => import('./pages/FeedbackRoadmap'));
const Blog = React.lazy(() => import('./pages/Blog'));
const BlogPost = React.lazy(() => import('./pages/BlogPost'));
const SignaturePreviewTemplate = React.lazy(() => import('./pages/SignaturePreviewTemplate'));
const BannerExportTest = React.lazy(() => import('./components/BannerExportTest').then(m => ({ default: m.BannerExportTest })));
const AdminLogin = React.lazy(() => import('./pages/AdminLogin'));
const AdminPanel = React.lazy(() => import('./pages/AdminPanel'));
const AdminGmailOAuthTest = React.lazy(() => import('./pages/AdminGmailOAuthTest'));
const GmailCallback = React.lazy(() => import('./pages/GmailCallback'));
const MicrosoftCallback = React.lazy(() => import('./pages/MicrosoftCallback'));
const BusinessCardLandingPage = React.lazy(() => import('./pages/BusinessCardLandingPage'));
const DigitalCardShare = React.lazy(() => import('./pages/DigitalCardShare'));
const DigitalBusinessCardsLanding = React.lazy(() => import('./pages/DigitalBusinessCardsLanding'));
const TeamManagementFeatures = React.lazy(() => import('./pages/TeamManagementFeatures'));
const MyProfile = React.lazy(() => import('./pages/MyProfile'));
const FreeDigitalCardLanding = React.lazy(() => import('./pages/FreeDigitalCardLanding'));
const AddToAppleWallet = React.lazy(() => import('./pages/AddToAppleWallet'));
const AddToGoogleWallet = React.lazy(() => import('./pages/AddToGoogleWallet'));
const WebhookZapierGuide = React.lazy(() => import('./pages/docs/WebhookZapierGuide'));
const WebhookMakeGuide = React.lazy(() => import('./pages/docs/WebhookMakeGuide'));
const WebhookCustomListenerGuide = React.lazy(() => import('./pages/docs/WebhookCustomListenerGuide'));
const AcceptInvitation = React.lazy(() => import('./pages/AcceptInvitation'));
const IntentOnboarding = React.lazy(() => import('./pages/IntentOnboarding'));
const EngagementLanding = React.lazy(() => import('./pages/EngagementLanding'));
const PassLanding = React.lazy(() => import('./pages/PassLanding'));
const RecipientsPage = React.lazy(() => import('./pages/RecipientsPage'));
const DigitalWalletMembershipCards = React.lazy(() => import('./pages/blog/DigitalWalletMembershipCards'));
const WalletLoyaltyWithoutApp = React.lazy(() => import('./pages/blog/WalletLoyaltyWithoutApp'));
const EmailSignatureLoyalty = React.lazy(() => import('./pages/blog/EmailSignatureLoyalty'));
const TeamSignatureOverpaying = React.lazy(() => import('./pages/blog/TeamSignatureOverpaying'));
const SignaturePolicyComplianceGuide = React.lazy(() => import('./pages/blog/SignaturePolicyComplianceGuide'));
const SignatureForwardVsExclaimer = React.lazy(() => import('./pages/blog/SignatureForwardVsExclaimer'));
const WalletPassesLanding = React.lazy(() => import('./pages/WalletPassesLanding'));
const LoyaltyLanding = React.lazy(() => import('./pages/LoyaltyLanding'));
const SalonLoyalty = React.lazy(() => import('./pages/loyalty/SalonLoyalty'));
const FoodTruckLoyalty = React.lazy(() => import('./pages/loyalty/FoodTruckLoyalty'));
const GymLoyalty = React.lazy(() => import('./pages/loyalty/GymLoyalty'));
const SquareLoyaltyComparison = React.lazy(() => import('./pages/vs/SquareLoyaltyComparison'));
const StampMeComparison = React.lazy(() => import('./pages/vs/StampMeComparison'));
const LoopyLoyaltyComparison = React.lazy(() => import('./pages/vs/LoopyLoyaltyComparison'));
const ExclaimerComparison = React.lazy(() => import('./pages/vs/ExclaimerComparison'));
const HiHelloComparison = React.lazy(() => import('./pages/vs/HiHelloComparison'));
const IntegrationsLanding = React.lazy(() => import('./pages/IntegrationsLanding'));
const MerchantDashboard = React.lazy(() => import('./pages/MerchantDashboard'));
const ShopifyIntegration = React.lazy(() => import('./pages/ShopifyIntegration'));
const ShopifyCallback = React.lazy(() => import('./pages/ShopifyCallback'));
const GoogleWorkspaceCallback = React.lazy(() => import('./pages/GoogleWorkspaceCallback'));
const MemberPage = React.lazy(() => import('./pages/MemberPage'));

const PageLoader = (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <CircularProgress />
  </Box>
);

const AppRoutes: React.FC = () => {
  // Check window.location FIRST before React Router location to avoid hook issues
  const actualPathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const isAcceptInvitation = actualPathname === '/accept-invitation';
  const isPublicCardPage = actualPathname.startsWith('/card/');
  const isPassLandingPage = actualPathname.startsWith('/p/');
  const isMemberPage = actualPathname.startsWith('/member/');

  // Only call useLocation() for routes that need it
  const location = useLocation();

  // Track page views on route change (skip for /accept-invitation and pass landing pages)
  // Pass landing pages have their own server-side tracking to avoid duplicates
  React.useEffect(() => {
    if (location.pathname !== '/accept-invitation' && !location.pathname.startsWith('/p/') && !location.pathname.startsWith('/member/')) {
      trackPageView(location.pathname + location.search);
    }
  }, [location]);

  // On public card pages, render routes without Clerk components
  if (isPublicCardPage) {
    return (
      <div className="min-h-screen bg-white">
        <main>
          <Suspense fallback={PageLoader}>
            <Routes>
              <Route path="/card/:slug" element={<BusinessCardLandingPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </main>
      </div>
    );
  }

  // On pass landing pages, render without Clerk for universal access
  if (isPassLandingPage) {
    return (
      <div className="min-h-screen">
        <main>
          <Suspense fallback={PageLoader}>
            <Routes>
              <Route path="/p/:passId" element={<PassLanding />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </main>
      </div>
    );
  }

  // On member pages (managed member detail form + signature), render without Clerk
  if (isMemberPage) {
    return (
      <div className="min-h-screen">
        <main>
          <Suspense fallback={PageLoader}>
            <Routes>
              <Route path="/member/:token" element={<MemberPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </main>
      </div>
    );
  }

  // All other routes require Clerk
  return (
    <div className="min-h-screen bg-white">
      <main>
        <ClerkLoading>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
            <CircularProgress />
          </Box>
        </ClerkLoading>

        <ClerkLoaded>
          <Suspense fallback={PageLoader}>
            <Routes>
              {/* Accept invitation route - FIRST to prevent catch-all */}
              <Route
                path="/accept-invitation"
                element={
                  <ClerkLoaded fallback={
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                      <CircularProgress />
                      <Typography sx={{ mt: 2 }}>Loading...</Typography>
                    </Box>
                  }>
                    <AcceptInvitation />
                  </ClerkLoaded>
                }
              />

              {/* Public routes */}
              <Route path="/" element={<Home />} />
              <Route path="/pricing" element={<Pricing />} />
              {/* Generator routes wrapped with OnboardingGuard to capture intent params */}
              {/* OnboardingGuard allows non-signed-in users through, only processes intent when signed in */}
              <Route path="/generator" element={
                <OnboardingGuard skipCheck>
                  <CreateSignature />
                </OnboardingGuard>
              } />
              <Route path="/generator/:signatureId" element={
                <OnboardingGuard skipCheck>
                  <CreateSignature />
                </OnboardingGuard>
              } />
              <Route path="/support" element={<Support />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="/contact-us" element={<ContactUs />} />
              <Route path="/feedback-roadmap" element={<FeedbackRoadmap />} />
              <Route path="/blog" element={<Blog />} />
              {/* SEO-optimized blog posts with dedicated pages */}
              <Route path="/blog/digital-wallet-membership-cards" element={<DigitalWalletMembershipCards />} />
              <Route path="/blog/wallet-loyalty-without-app" element={<WalletLoyaltyWithoutApp />} />
              <Route path="/blog/email-signature-loyalty-program" element={<EmailSignatureLoyalty />} />
              <Route path="/blog/email-signature-loyalty" element={<Navigate to="/blog/email-signature-loyalty-program" replace />} />
              <Route path="/blog/team-email-signature-overpaying" element={<TeamSignatureOverpaying />} />
              <Route path="/blog/email-signature-policy-compliance-guide" element={<SignaturePolicyComplianceGuide />} />
              <Route path="/blog/signatureforward-vs-exclaimer" element={<SignatureForwardVsExclaimer />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/add-to-apple-wallet" element={<AddToAppleWallet />} />
              <Route path="/add-to-google-wallet" element={<AddToGoogleWallet />} />
              <Route path="/docs/automation/webhook-zapier" element={<WebhookZapierGuide />} />
              <Route path="/docs/automation/webhook-make" element={<WebhookMakeGuide />} />
              <Route path="/docs/automation/webhook-custom-listener" element={<WebhookCustomListenerGuide />} />
              <Route path="/preview-template/:templateId" element={<SignaturePreviewTemplate />} />
              <Route path="/preview-template/saved/:templateId" element={<SignaturePreviewTemplate />} />
              {/* Feature Landing Pages (Public) */}
              <Route path="/digital-business-cards" element={<DigitalBusinessCardsLanding />} />
              <Route path="/wallet-passes" element={<WalletPassesLanding />} />
              <Route path="/loyalty" element={<LoyaltyLanding />} />
              <Route path="/loyalty/salons" element={<SalonLoyalty />} />
              <Route path="/loyalty/salon" element={<Navigate to="/loyalty/salons" replace />} />
              <Route path="/loyalty/food-trucks" element={<FoodTruckLoyalty />} />
              <Route path="/loyalty/food-truck" element={<Navigate to="/loyalty/food-trucks" replace />} />
              <Route path="/loyalty/gyms" element={<GymLoyalty />} />
              <Route path="/loyalty/gym" element={<Navigate to="/loyalty/gyms" replace />} />
              <Route path="/vs/square-loyalty" element={<SquareLoyaltyComparison />} />
              <Route path="/vs/stamp-me" element={<StampMeComparison />} />
              <Route path="/vs/stampme" element={<Navigate to="/vs/stamp-me" replace />} />
              <Route path="/vs/loopy-loyalty" element={<LoopyLoyaltyComparison />} />
              <Route path="/vs/exclaimer" element={<ExclaimerComparison />} />
              <Route path="/vs/hihello" element={<HiHelloComparison />} />
              <Route path="/integrations" element={<IntegrationsLanding />} />
              <Route path="/team-management-features" element={<TeamManagementFeatures />} />
              <Route path="/free" element={<FreeDigitalCardLanding />} />
              <Route path="/engagement" element={<EngagementLanding />} />
              {/* Merchant verification dashboard - uses API key auth */}
              <Route path="/merchant" element={<MerchantDashboard />} />

              {/* Campaign Manager - Redirect to Dashboard Campaigns tab */}
              <Route
                path="/campaigns"
                element={
                  <SignedIn>
                    <Navigate to="/dashboard?tab=campaigns" replace />
                  </SignedIn>
                }
              />

              {/* Intent Onboarding - accessible to signed-in users */}
              <Route
                path="/onboarding"
                element={
                  <SignedIn>
                    <IntentOnboarding />
                  </SignedIn>
                }
              />

              {/* Pass Only Wizard - streamlined flow for wallet-pass-only users */}
              <Route
                path="/create-pass"
                element={
                  <SignedIn>
                    <PassOnlyWizard />
                  </SignedIn>
                }
              />

              {/* Protected routes - wrapped with OnboardingGuard to redirect new users */}
              <Route
                path="/dashboard"
                element={
                  <SignedIn>
                    <OnboardingGuard>
                      <Dashboard />
                    </OnboardingGuard>
                  </SignedIn>
                }
              />
              <Route
                path="/dashboard/recipients/:passId"
                element={
                  <SignedIn>
                    <RecipientsPage />
                  </SignedIn>
                }
              />
              <Route
                path="/analytics"
                element={
                  <SignedIn>
                    <Analytics />
                  </SignedIn>
                }
              />
              <Route
                path="/tracking-signatures"
                element={
                  <SignedIn>
                    <DynamicSignatures />
                  </SignedIn>
                }
              />
              <Route
                path="/signature/:signatureId"
                element={
                  <SignedIn>
                    <SignatureView />
                  </SignedIn>
                }
              />
              <Route
                path="/test"
                element={
                  <SignedIn>
                    <Test />
                  </SignedIn>
                }
              />

              <Route
                path="/outlook-test"
                element={
                  <SignedIn>
                    <OutlookSignatureTest />
                  </SignedIn>
                }
              />

              <Route
                path="/banner-test"
                element={
                  <SignedIn>
                    <BannerExportTest />
                  </SignedIn>
                }
              />

              {/* Checkout success route - no auth required */}
              <Route path="/checkout-success" element={<CheckoutSuccess />} />

              {/* Subscription management - requires auth */}
              <Route
                path="/manage-subscription"
                element={
                  <SignedIn>
                    <ManageSubscription />
                  </SignedIn>
                }
              />
              {/* Team management - requires auth & Business+ */}
              <Route
                path="/team-management"
                element={
                  <SignedIn>
                    <TeamManagement />
                  </SignedIn>
                }
              />
              <Route
                path="/digital-card"
                element={
                  <SignedIn>
                    <OnboardingGuard>
                      <DigitalCardShare />
                    </OnboardingGuard>
                  </SignedIn>
                }
              />
              <Route
                path="/my-profile"
                element={
                  <SignedIn>
                    <MyProfile />
                  </SignedIn>
                }
              />

              {/* Shopify Integration — Pro+ */}
              <Route
                path="/integrations/shopify"
                element={
                  <SignedIn>
                    <ShopifyIntegration />
                  </SignedIn>
                }
              />
              {/* Shopify OAuth callback */}
              <Route path="/auth/shopify/callback" element={<ShopifyCallback />} />

              {/* Google Workspace OAuth callback */}
              <Route path="/google-workspace-callback" element={<GoogleWorkspaceCallback />} />

              {/* Gmail OAuth callback route - requires auth */}
              <Route path="/gmail-callback" element={<GmailCallback />} />

              {/* Microsoft OAuth callback route - requires auth */}
              <Route path="/auth/microsoft/callback" element={<MicrosoftCallback />} />

              {/* Clerk SSO callback - handles OAuth redirects from Google/Microsoft sign-in */}
              <Route path="/sso-callback" element={<AuthenticateWithRedirectCallback />} />

              {/* Admin routes - dev/staging only, removed from production builds */}
              {import.meta.env.DEV && (
                <>
                  <Route path="/admin" element={<AdminLogin />} />
                  <Route path="/admin/panel" element={<AdminPanel />} />
                  <Route path="/admin/gmail-oauth-test" element={<AdminGmailOAuthTest />} />
                </>
              )}

              {/* Fallback route */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </ClerkLoaded>
      </main>
    </div>
  );
};

export default AppRoutes;
